import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  TutorialContextType,
  TutorialState,
  TutorialPage,
  TutorialStorageData,
  TUTORIAL_STORAGE_KEY,
  TOTAL_TUTORIAL_PAGES,
  TOTAL_MOBILE_TUTORIAL_PAGES
} from '../types/tutorial';
import { localStorageUtil } from '../utils/localStorage';
import { createLogger } from '../utils/logger';

const logger = createLogger('TutorialContext');

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export interface TutorialProviderProps {
  children: React.ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  // Initialize tutorial state from localStorage
  const [tutorialState, setTutorialState] = useState<TutorialState>(() => {
    const storedData = localStorageUtil.getItem<TutorialStorageData>(TUTORIAL_STORAGE_KEY);

    return {
      isActive: false,
      currentPage: 1,
      hasCompleted: storedData?.completed ?? false
    };
  });

  // Save tutorial completion to localStorage
  const saveTutorialCompletion = useCallback((completed: boolean, lastPage?: TutorialPage) => {
    const data: TutorialStorageData = {
      completed,
      timestamp: new Date().toISOString(),
      lastPage
    };

    localStorageUtil.setItem(TUTORIAL_STORAGE_KEY, data);

    if (logger.isEnabled()) {
      logger.info('Tutorial state saved', { completed, lastPage });
    }
  }, []);

  // Start the tutorial
  const startTutorial = useCallback(() => {
    setTutorialState({
      isActive: true,
      currentPage: 1,
      hasCompleted: false
    });

    if (logger.isEnabled()) {
      logger.info('Tutorial started');
    }
  }, []);

  // Move to next page
  const nextPage = useCallback(() => {
    setTutorialState(prev => {
      // Support both desktop (5 pages) and mobile (6 pages) tutorials
      const maxPage = TOTAL_MOBILE_TUTORIAL_PAGES;

      if (prev.currentPage >= maxPage) {
        // At last page, can't advance further
        return prev;
      }

      const nextPage = (prev.currentPage + 1) as TutorialPage;

      if (logger.isEnabled()) {
        logger.info('Tutorial page advanced', { from: prev.currentPage, to: nextPage });
      }

      return {
        ...prev,
        currentPage: nextPage
      };
    });
  }, []);

  // Move to previous page
  const prevPage = useCallback(() => {
    setTutorialState(prev => {
      if (prev.currentPage <= 1) {
        return prev;
      }

      const prevPage = (prev.currentPage - 1) as TutorialPage;

      if (logger.isEnabled()) {
        logger.info('Tutorial page reversed', { from: prev.currentPage, to: prevPage });
      }

      return {
        ...prev,
        currentPage: prevPage
      };
    });
  }, []);

  // Go to specific page
  const goToPage = useCallback((page: TutorialPage) => {
    setTutorialState(prev => ({
      ...prev,
      currentPage: page
    }));

    if (logger.isEnabled()) {
      logger.info('Tutorial jumped to page', { page });
    }
  }, []);

  // Skip tutorial (close without completing)
  const skipTutorial = useCallback(() => {
    setTutorialState(prev => ({
      ...prev,
      isActive: false,
      hasCompleted: true
    }));

    // Mark as completed so it doesn't show again
    saveTutorialCompletion(true, tutorialState.currentPage);

    if (logger.isEnabled()) {
      logger.info('Tutorial skipped', { currentPage: tutorialState.currentPage });
    }
  }, [saveTutorialCompletion, tutorialState.currentPage]);

  // Complete tutorial (finish successfully)
  const completeTutorial = useCallback(() => {
    setTutorialState({
      isActive: false,
      currentPage: 1,
      hasCompleted: true
    });

    saveTutorialCompletion(true, TOTAL_TUTORIAL_PAGES as TutorialPage);

    if (logger.isEnabled()) {
      logger.info('Tutorial completed');
    }
  }, [saveTutorialCompletion]);

  // Reset tutorial (for testing/debugging)
  const resetTutorial = useCallback(() => {
    setTutorialState({
      isActive: false,
      currentPage: 1,
      hasCompleted: false
    });

    localStorageUtil.removeItem(TUTORIAL_STORAGE_KEY);

    if (logger.isEnabled()) {
      logger.info('Tutorial reset');
    }
  }, []);

  // Auto-complete on last page when advancing
  useEffect(() => {
    // Note: We don't auto-complete anymore, letting the individual overlay
    // components (desktop/mobile) handle completion logic
    // This is kept for potential future use
  }, [tutorialState.isActive, tutorialState.currentPage]);

  const contextValue = useMemo<TutorialContextType>(() => ({
    tutorialState,
    startTutorial,
    nextPage,
    prevPage,
    goToPage,
    skipTutorial,
    completeTutorial,
    resetTutorial
  }), [
    tutorialState,
    startTutorial,
    nextPage,
    prevPage,
    goToPage,
    skipTutorial,
    completeTutorial,
    resetTutorial
  ]);

  return (
    <TutorialContext.Provider value={contextValue}>
      {children}
    </TutorialContext.Provider>
  );
};

// Hook to use tutorial context
export const useTutorial = (): TutorialContextType => {
  const context = useContext(TutorialContext);

  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }

  return context;
};

export default TutorialProvider;
